import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import { IntlShape, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { translate } from 'containers/services/translate';
import { COLORS } from 'constants/design';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import { get } from 'lodash';
import { Listings } from 'containers/AppContainer/typings';
import { PROVISION_INTERVENANT_FIELDS } from 'containers/RecapManagerPage/constants';
import { toast } from 'react-toastify';
import {
  getStakeHolders,
  getStakeHoldersTypes,
} from 'containers/CourtManagerLegalProceedings/Provision/utils';
import messages from 'containers/CourtManagerLegalProceedings/Provision/messages';
import { Autocomplete } from '@material-ui/lab';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import currentUserStorage from 'containers/services/security/current-user-storage';

export interface StakeHolderDialogProps {
  intl: IntlShape;
  open: boolean;
  onClose: Function;
  listings?: Listings;
  riskId: string | undefined;
  claimId: number;
  requestCreateStakeHolder?: Function;
  claimNumber?: String;
  setUpdateCurrentRisk: Function;
  provisions: any;
}
const StyledDiv = styled.div`
  width: 576px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .input {
    width: 90%;
    font-size: 13px;
    margin-bottom: 10px;
  }
`;
const StyledDialogActions = styled(DialogActions)`
  display: flex;
  padding: 20px 30px;
  justify-content: space-between;

  .moveButton {
    border: 1px solid ${COLORS.YELLOW};
    background-color: ${COLORS.YELLOW};
    color: ${COLORS.LIMED_SPURCE};
    &:hover {
      background-color: ${COLORS.YELLOW};
    }
  }

  .Mui-disabled {
    opacity: 0.6;
  }
`;

const StakeHolderDialog: React.FC<StakeHolderDialogProps> = (
  props,
): JSX.Element => {
  const { onClose, open, riskId } = props;
  /* istanbul ignore next */
  const handleValidation = () => {
    props.setUpdateCurrentRisk(false);
    props.requestCreateStakeHolder &&
      props.requestCreateStakeHolder(
        {
          ...stakeHolder,
          riskId,
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
          claimNumber: props.claimNumber,
          claimId: props.claimId,
          isManualEvaluation: false,
          isNewStakeHolder: true,
          createdBy: currentUserStorage?.get()?.username,
        },
        successCallBack,
      );
    setStakeHolder({});
    onClose(false);
  };

  /* istanbul ignore next */
  const successCallBack = () => {
    props.setUpdateCurrentRisk(true);
    toast.success(translate(messages.addStakeHolderSuccessMessage, props));
  };

  const [stakeHolder, setStakeHolder] = useState({} as any);
  /* istanbul ignore next */
  const handleChange = (
    event: React.ChangeEvent<{
      value: any;
      name?: string;
    }>,
    valueField?: any,
  ) => {
    const { value, name } = event.target;
    if (!!valueField) {
      const contractorCode = get(valueField, 'ItemKey', '');
      setStakeHolder({
        ...stakeHolder,
        [PROVISION_INTERVENANT_FIELDS.CONTRACTOR_CODE]: contractorCode,
      });
    }
    name && setStakeHolder({ ...stakeHolder, [name]: value });
  };
  return (
    <Dialog
      onClose={
        /* istanbul ignore next */ () => {
          onClose(false);
          setStakeHolder({});
        }
      }
      aria-labelledby="simple-dialog-title"
      open={open}
    >
      <DialogTitle>
        {translate({ ...messages.addStakeHolder }, props)}
      </DialogTitle>
      <DialogContent>
        <StyledDiv>
          <FormControl className="input">
            <InputLabel>
              {translate(messages.stakeHolderType, props)}
            </InputLabel>
            <Select
              labelWidth={60}
              value={get(
                stakeHolder,
                PROVISION_INTERVENANT_FIELDS.CONTRACTOR_TYPE_CODE,
              )}
              onChange={handleChange}
              name={PROVISION_INTERVENANT_FIELDS.CONTRACTOR_TYPE_CODE}
              id={PROVISION_INTERVENANT_FIELDS.CONTRACTOR_TYPE_CODE}
            >
              {getStakeHoldersTypes(messages, props).map((searchType: any) => (
                <MenuItem value={searchType?.id}>{searchType?.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className="input">
            <Autocomplete
              options={getStakeHolders(
                get(props.listings, 'intervenants', []),
                stakeHolder,
                props.provisions,
              )}
              getOptionLabel={
                /* istanbul ignore next */ (option: any) =>
                  `${option?.ItemDescription ? option?.ItemDescription : ''}`
              }
              value={
                get(props.listings, 'intervenants', []).find(
                  item =>
                    item.ItemKey ===
                    get(
                      stakeHolder,
                      PROVISION_INTERVENANT_FIELDS.CONTRACTOR_CODE,
                    ),
                ) as any
              }
              onChange={handleChange as any}
              renderInput={
                /* istanbul ignore next */ params => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={translate(messages.stakeHolder, props)}
                  />
                )
              }
            />
          </FormControl>
          <FormControl className="input">
            <TextField
              type="number"
              label={translate(messages.stakeHolderAmount, props)}
              name={PROVISION_INTERVENANT_FIELDS.AMOUNT}
              value={get(stakeHolder, PROVISION_INTERVENANT_FIELDS.AMOUNT)}
              onChange={handleChange}
            />
          </FormControl>
        </StyledDiv>
      </DialogContent>
      <StyledDialogActions>
        <SecondaryButton
          onClick={
            /* istanbul ignore next */ () => {
              onClose(false);
            }
          }
          disableElevation
          title={translate({ ...messages.cancelStakeHolderDialog }, props)}
        />
        <PrimaryButton
          onClick={handleValidation}
          disableElevation
          disabled={false}
          title={translate(
            {
              ...messages.confirmStakeHolderDialog,
            },
            props,
          )}
        />
      </StyledDialogActions>
    </Dialog>
  );
};

StakeHolderDialog.defaultProps = {
  open: false,
};

export default injectIntl(StakeHolderDialog);
